import { useCallback, useEffect, useRef, useState } from "react";

const useHttpWithCache = (config = {}) => {
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState(false);
  const [errorStatus, setErrorStatus] = useState(null);

  const {
    requestCallback = async () => {
      throw new Error("useHttpWithCache: requestCallback is required");
    },
    reduxSetter = () => {},
    reduxCash = {},
    reduxReset = () => {},
    allowToCash = true,
  } = config;

  const mountedRef    = useRef(true);
  const controllerRef = useRef(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (controllerRef.current && typeof controllerRef.current.abort === "function") {
        controllerRef.current.abort();
      }
    };
  }, []);

  const cancel = useCallback(() => {
    if (controllerRef.current && typeof controllerRef.current.abort === "function") {
      controllerRef.current.abort();
    }
  }, []);

  const run = useCallback(
    async (force = false) => {
      const hasNulls =
        reduxCash &&
        Object.entries(reduxCash).some(([, v]) => v === null);

      if (!force && allowToCash && !hasNulls) {
        if (mountedRef.current) {
          setLoading(false);
          setError(false);
        }
        reduxSetter(reduxCash);
        return reduxCash;
      }

      if (!allowToCash) reduxReset();

      // start fresh request
      cancel();
      const ac = new AbortController();
      controllerRef.current = ac;

      if (mountedRef.current) {
        setLoading(true);
        setError(false);
        setErrorStatus(null);
      }

      try {
        const res = await requestCallback({ signal: ac.signal });

        if (mountedRef.current) {
          setLoading(false);
          reduxSetter(res);
        }
        return res;
      } catch (err) {
        if (err && err.name === "AbortError") return;

        if (mountedRef.current) {
          setLoading(false);
          setError(true);
          setErrorStatus(err);
        }
        return err;
      } finally {
        if (controllerRef.current === ac) controllerRef.current = null;
      }
    },
    [allowToCash, reduxCash, reduxReset, reduxSetter, requestCallback, cancel]
  );

  const request = useCallback(() => run(false), [run]);

  const refresh = useCallback(() => {
    reduxReset();
    return run(true);
  }, [reduxReset, run]);

  return { loading, error, errorStatus, request, refresh, cancel };
};

export { useHttpWithCache };
